import React, { useEffect, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { sportslist, sportwisetypeparameterlist } from "../../../services/master";
import { funderprofile } from "../../../services/funder";
import { classSportsAssessment } from "../../../services/superadmin";
import dotracking from "../../trackinglogs";
import trackobj from "../../trackingpages.json";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const CHART_OPTIONS = {
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: false },
  },
  scales: {
    y: { beginAtZero: true },
  },
};

const PHASE_COLORS = {
  average: "rgba(54, 162, 235, 0.6)",
  maximum: "rgba(75, 192, 192, 0.6)",
  minimum: "rgba(255, 159, 64, 0.6)",
};

const buildPhaseChart = (phaseData, labelPrefix) => {
  if (!phaseData?.labels?.length) return null;

  return {
    labels: phaseData.labels,
    datasets: [
      {
        label: `${labelPrefix} Average`,
        data: phaseData.average || [],
        backgroundColor: PHASE_COLORS.average,
      },
      {
        label: `${labelPrefix} Maximum`,
        data: phaseData.maximum || [],
        backgroundColor: PHASE_COLORS.maximum,
      },
      {
        label: `${labelPrefix} Minimum`,
        data: phaseData.minimum || [],
        backgroundColor: PHASE_COLORS.minimum,
      },
    ],
  };
};
function PhaseChart({ title, phaseData }) {
  const chart = buildPhaseChart(phaseData, title);

  return (
    <div className="col-md-6 mb-3">
      <div className="card card-body">
        <h5>{title}</h5>
        {chart ? (
          <Bar data={chart} options={CHART_OPTIONS} />
        ) : (
          <p className="text-muted mb-0">No {title.toLowerCase()} data</p>
        )}
      </div>
    </div>
  );
}

function GenderGraphRow({ label, group }) {
  const data = group || {};
  return (
    <div className="mb-2">
      <div className="row">
        <PhaseChart title={`Baseline - ${label}`} phaseData={data.baseline} />
        <PhaseChart title={`Endline - ${label}`} phaseData={data.endline} />
      </div>
    </div>
  );
}

function AssessmentResults({ data }) {
  if (!data) return null;

  const allGroup = data.all || {
    baseline: data.baseline,
    endline: data.endline,
  };
  const girlsGroup = data.girls || {};
  const boysGroup = data.boys || {};

  return (
    <div className="mb-4 mt-3">
      <GenderGraphRow label="All" group={allGroup} />
      <GenderGraphRow label="Girls" group={girlsGroup} />
      <GenderGraphRow label="Boys" group={boysGroup} />
    </div>
  );
}

export const ClassSportsAssessment = () => {
  const [funderId, setFunderId] = useState("");
  const [slist, setSList] = useState([]);
  const [tlist, setTList] = useState([]);
  const [usport, setUSport] = useState("");
  const [asstype, setAssType] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let active = true;

    Promise.all([sportslist(), funderprofile()]).then(([sportsRes, funderRes]) => {
      if (!active) return;
      if (sportsRes?.status) setSList(sportsRes.data || []);
      if (funderRes?.status) setFunderId(funderRes.data.funder_id);
    });

    return () => {
      active = false;
    };
  }, []);

  const resetResults = useCallback(() => {
    setAssessmentData(null);
    setErrorMsg("");
  }, []);

  const loadsport = useCallback(
    (e) => {
      const sportId = e.target.value;
      setUSport(sportId);
      setAssType("");
      setTList([]);
      resetResults();

      if (!sportId) return;

      sportwisetypeparameterlist(sportId).then((response) => {
        if (response?.status) {
          setTList(response.data.sportsParameters || []);
        }
      });
    },
    [resetResults]
  );

  const validateFilters = () => {
    if (!usport) return "Please select Sport";
    if (!asstype) return "Please select Parameter Type";
    if (!year) return "Please select Year";
    if (!funderId) return "Funder profile not loaded";
    return "";
  };

  const loadAssessment = async () => {
    const validationError = validateFilters();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    dotracking(
      "class sports assessment filter $$ " + trackobj.assessmentheading
    );

    setLoading(true);
    resetResults();

    try {
      const temp = new FormData();
      temp.append("funder_id", funderId);
      temp.append("sports_id", usport);
      temp.append("assessment_type_id", asstype);
      temp.append("year", year);

      const res = await classSportsAssessment(temp);
      if (res?.status && res.data) {
        setAssessmentData(res.data);
      } else {
        setErrorMsg(res?.message || "No assessment data found");
      }
    } catch (err) {
      setErrorMsg("Failed to load class sports assessment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="side-app">
      <div className="page-header">
        <div className="breadcrumb">
          <h4 className="mb-0">Class Sports Assessment</h4>
        </div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Sports Performance</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Class Sports Assessment
          </li>
        </ol>
      </div>

      <div className="row">
        <div className="col-12">
          <form className="card mt-0" onSubmit={(e) => e.preventDefault()}>
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label>
                      Sports <span className="errortext">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={usport}
                      onChange={loadsport}
                    >
                      <option value="" disabled>
                        --Select Sports--
                      </option>
                      {slist.map((item) => (
                        <option
                          key={item.sports_cat_uid}
                          value={item.sports_cat_uid}
                        >
                          {item.sports_category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label>
                      Parameter Type <span className="errortext">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={asstype}
                      onChange={(e) => setAssType(e.target.value)}
                    >
                      <option value="" disabled>
                        --Select Parameter Type--
                      </option>
                      {tlist.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.parameter_type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <label>
                      Year <span className="errortext">*</span>
                    </label>
                    <select
                      className="form-control"
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                    >
                      <option value="" disabled>
                        --Select Year--
                      </option>
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group mb-0">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={loadAssessment}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "View Assessment"}
                    </button>
                  </div>
                </div>
              </div>

              {errorMsg !== "" && (
                <div
                  className="alert alert-warning mt-3 mb-0"
                  style={{ color: "#856404" }}
                >
                  {errorMsg}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {assessmentData && (
        <div className="row">
          <div className="col-12">
            <AssessmentResults data={assessmentData} />
          </div>
        </div>
      )}
    </div>
  );
};
